/** User preferences. Small, flat, and entirely local. */

export const THEMES = ["system", "light", "dark", "minimal"] as const;
export type Theme = (typeof THEMES)[number];

/**
 * How you want recipes ranked. This is a *goal*, not a restriction — it never
 * hides anything, it changes the order.
 *
 * Split from `diet` below. They used to be one field with five values, which
 * meant choosing "vegan" silently gave up any macro preference and choosing
 * "cut" silently gave up the vegan filter. Two axes wearing one control.
 */
export const EATING_GOALS = ["maintain", "bulk", "cut"] as const;
export type EatingGoal = (typeof EATING_GOALS)[number];

/** What you won't eat. This one really does filter. */
export const DIETS = ["anything", "vegetarian", "vegan"] as const;
export type Diet = (typeof DIETS)[number];

export const EXPIRY_ALERTS = ["none", "expiring-only", "daily"] as const;
export type ExpiryAlert = (typeof EXPIRY_ALERTS)[number];

export const COOKING_LEVELS = ["beginner", "intermediate", "advanced"] as const;
export type CookingLevel = (typeof COOKING_LEVELS)[number];

export interface Settings {
  theme: Theme;
  /** Ranks recipes by their macros. */
  eating: EatingGoal;
  /** Filters recipes out entirely. */
  diet: Diet;
  /** How insistently the app points out food about to go off. */
  notificationFrequency: ExpiryAlert;
  cookingExperience: CookingLevel;
}

export const DEFAULT_SETTINGS: Settings = {
  // Following the operating system is the right default: it's what the app did
  // before there was a setting, and it's what most people expect.
  theme: "system",
  eating: "maintain",
  diet: "anything",
  notificationFrequency: "expiring-only",
  cookingExperience: "beginner",
};

export const THEME_LABELS: Record<Theme, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
  minimal: "Minimal",
};

export const EATING_LABELS: Record<EatingGoal, string> = {
  maintain: "Maintain",
  bulk: "Bulk",
  cut: "Cut",
};

export const DIET_LABELS: Record<Diet, string> = {
  anything: "Anything",
  vegetarian: "Vegetarian",
  vegan: "Vegan",
};

export const ALERT_LABELS: Record<ExpiryAlert, string> = {
  none: "Never",
  "expiring-only": "When something's going off",
  daily: "Always",
};

export const COOKING_LABELS: Record<CookingLevel, string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

/** How hard a recipe may be at each level. Higher tiers include the easier ones. */
export const LEVEL_RANK: Record<CookingLevel, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
};
